import React, { useState, useRef } from 'react';
import {
  Palette,
  UploadCloud,
  RotateCcw,
  Check,
  Trash2,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { useBranding, DEFAULT_BRANDING } from '@/context/BrandingContext.js';

interface UploadZoneProps {
  title: string;
  subtitle: string;
  acceptedFormats: string;
  maxSizeMb: number;
  currentValue: string | null;
  onSelect: (base64Url: string | null) => void;
  backgroundType?: 'light' | 'dark' | 'browser';
  workspaceName?: string;
}

function UploadZone({
  title,
  subtitle,
  acceptedFormats,
  maxSizeMb,
  currentValue,
  onSelect,
  backgroundType = 'light',
  workspaceName = 'Techosolution',
}: UploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const processFile = (file: File) => {
    setError(null);

    // Validate size
    const maxBytes = maxSizeMb * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`File is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum allowed is ${maxSizeMb}MB.`);
      return;
    }

    // Validate format
    const validExtensions = acceptedFormats.split(',').map((s) => s.trim().toLowerCase());
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    const isExtensionMatch = validExtensions.some((ext) => ext === fileExt || file.type.includes(ext.replace('.', '')));

    if (!isExtensionMatch && !file.type.startsWith('image/')) {
      setError(`Unsupported format. Accepted: ${acceptedFormats}`);
      return;
    }

    // Safety validation for SVG files
    if (fileExt === '.svg' || file.type.includes('svg')) {
      const textReader = new FileReader();
      textReader.onload = () => {
        const text = String(textReader.result || '');
        if (/<script[\s>]/i.test(text) || /javascript:/i.test(text) || /on[a-z]+\s*=/i.test(text)) {
          setError('SVG contains unsafe script or event handler elements. Please upload a clean vector image.');
          return;
        }
        const dataReader = new FileReader();
        dataReader.onload = () => {
          if (typeof dataReader.result === 'string') {
            onSelect(dataReader.result);
          }
        };
        dataReader.onerror = () => {
          setError('Failed to read image file.');
        };
        dataReader.readAsDataURL(file);
      };
      textReader.onerror = () => {
        setError('Failed to validate SVG content.');
      };
      textReader.readAsText(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        onSelect(reader.result);
      }
    };
    reader.onerror = () => {
      setError('Failed to read image file.');
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  return (
    <div className="flex flex-col h-full rounded-xl border border-brand-border bg-surface p-4 shadow-sm transition-all hover:border-brand-teal/40">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h4 className="text-sm font-bold text-brand-ink">{title}</h4>
          <p className="text-xs text-brand-muted mt-0.5">{subtitle}</p>
        </div>
        <Badge variant="outline" className="text-[10px] text-brand-muted shrink-0">
          Max {maxSizeMb}MB
        </Badge>
      </div>

      {/* Visual Live Preview Box */}
      <div className="mt-2 mb-3">
        {backgroundType === 'browser' ? (
          /* Mock Browser Tab Preview for Favicon */
          <div className="rounded-lg border border-slate-300 bg-slate-100 p-2 shadow-inner">
            <div className="text-[10px] text-slate-500 font-semibold mb-1 flex items-center gap-1">
              <span>Live Browser Tab Simulation</span>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-t-md border-t border-x border-slate-300 px-3 py-1.5 shadow-sm max-w-[240px]">
              {currentValue ? (
                <img
                  src={currentValue}
                  alt="Favicon preview"
                  className="h-4 w-4 object-contain shrink-0"
                />
              ) : (
                <div className="h-4 w-4 rounded-sm bg-[#087F75] text-[9px] font-black text-white flex items-center justify-center shrink-0">
                  T
                </div>
              )}
              <span className="text-xs font-medium text-slate-800 truncate">
                {workspaceName} Project OS
              </span>
              <span className="text-[10px] text-slate-400 ml-auto cursor-default">×</span>
            </div>
          </div>
        ) : backgroundType === 'dark' ? (
          /* Dark Background Preview for Compact/Dark Logo */
          <div className="h-28 rounded-lg bg-[#073B38] border border-[#D2E1DB]/20 flex items-center justify-center p-3 relative overflow-hidden group shadow-inner">
            {currentValue ? (
              <img
                src={currentValue}
                alt={`${title} preview`}
                className="max-h-20 max-w-full object-contain filter drop-shadow-sm"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-center">
                <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-[#087F75] to-[#06685F] border border-[#C5EF79]/40 flex items-center justify-center font-black text-white text-sm shadow-md">
                  <span>TS</span>
                  <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[#C5EF79] ring-2 ring-[#073B38]" />
                </div>
                <span className="text-[11px] text-[#D2EBE4]/70 mt-1.5 font-medium">Default Compact Monogram</span>
              </div>
            )}
          </div>
        ) : (
          /* Light / Checkerboard Preview for Main Logo */
          <div className="h-28 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center p-3 relative overflow-hidden group shadow-inner">
            {currentValue ? (
              <img
                src={currentValue}
                alt={`${title} preview`}
                className="max-h-20 max-w-full object-contain filter drop-shadow-sm"
              />
            ) : (
              <div className="flex items-center space-x-3">
                <div className="relative h-11 w-11 rounded-xl bg-brand-teal flex items-center justify-center text-white font-bold text-lg shadow-md">
                  TS
                  <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-lime border-2 border-white" />
                </div>
                <div>
                  <div className="text-xs font-bold text-brand-ink">{workspaceName}</div>
                  <div className="text-[10px] text-brand-muted font-medium">Agency Work Management</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`flex-1 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-brand-teal bg-brand-tint/30 text-brand-ink'
            : 'border-slate-200 hover:border-brand-teal/60 hover:bg-slate-50/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats}
          onChange={handleFileChange}
          className="hidden"
        />
        <UploadCloud className="h-6 w-6 text-brand-teal mb-1.5" />
        <p className="text-xs font-semibold text-brand-ink">
          Drag & drop, or <span className="text-brand-teal underline">browse</span>
        </p>
        <p className="text-[11px] text-brand-muted mt-0.5">{acceptedFormats.replace(/\./g, ' ').toUpperCase()}</p>
      </div>

      {error && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-1.5">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{error}</span>
        </div>
      )}

      {/* Remove Button if custom asset uploaded */}
      {currentValue && (
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onSelect(null)}
            className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-7 px-2"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Remove Custom Asset
          </Button>
        </div>
      )}
    </div>
  );
}

export function BrandingSettingsSection() {
  const { branding, updateBranding, resetBranding } = useBranding();

  const [workspaceName, setWorkspaceName] = useState(branding.workspace_name || 'Techosolution');
  const [logoUrl, setLogoUrl] = useState<string | null>(branding.logo_url);
  const [darkLogoUrl, setDarkLogoUrl] = useState<string | null>(branding.dark_logo_url);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(branding.favicon_url);

  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Sync form inputs when branding updates from context
  React.useEffect(() => {
    setWorkspaceName(branding.workspace_name || 'Techosolution');
    setLogoUrl(branding.logo_url);
    setDarkLogoUrl(branding.dark_logo_url);
    setFaviconUrl(branding.favicon_url);
  }, [
    branding.workspace_name,
    branding.logo_url,
    branding.dark_logo_url,
    branding.favicon_url,
    branding.updated_at,
  ]);

  const hasUnsavedChanges =
    workspaceName !== (branding.workspace_name || 'Techosolution') ||
    logoUrl !== branding.logo_url ||
    darkLogoUrl !== branding.dark_logo_url ||
    faviconUrl !== branding.favicon_url;

  const handleSave = async () => {
    setIsSaving(true);
    setFeedback(null);
    try {
      await updateBranding({
        workspace_name: workspaceName.trim() || 'Techosolution',
        logo_url: logoUrl,
        dark_logo_url: darkLogoUrl,
        favicon_url: faviconUrl,
      });

      setFeedback({
        type: 'success',
        message: 'Workspace branding saved! Custom logo & favicon applied across the application.',
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err: any) {
      setFeedback({
        type: 'error',
        message: err?.message || 'Failed to update branding settings.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !confirm(
        'Are you sure you want to revert all workspace branding to defaults? This will restore the original Techosolution monogram and favicon.'
      )
    ) {
      return;
    }

    setIsResetting(true);
    setFeedback(null);
    try {
      await resetBranding();
      setWorkspaceName(DEFAULT_BRANDING.workspace_name);
      setLogoUrl(null);
      setDarkLogoUrl(null);
      setFaviconUrl(null);

      setFeedback({
        type: 'success',
        message: 'Workspace branding reset to default Techosolution theme.',
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err: any) {
      setFeedback({
        type: 'error',
        message: err?.message || 'Failed to reset branding settings.',
      });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Card className="border-brand-border shadow-sm overflow-hidden">
      <CardHeader className="border-b border-slate-100 pb-4 bg-gradient-to-r from-surface to-brand-tint/20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#087F75] to-[#06685F] text-white flex items-center justify-center shrink-0 shadow-sm">
              <Palette className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base font-bold text-brand-ink flex items-center gap-2">
                <span>Agency Branding &amp; Visual Assets</span>
                <Badge className="bg-[#087F75]/10 text-[#087F75] border-[#087F75]/20 font-semibold text-[10px]">
                  Owner Only
                </Badge>
              </CardTitle>
              <CardDescription className="text-xs text-brand-muted mt-0.5">
                Customize workspace identity, upload primary logos, dark/compact icons, and browser favicon.
              </CardDescription>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isResetting || isSaving}
              className="text-xs border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              <RotateCcw className={`h-3.5 w-3.5 mr-1.5 ${isResetting ? 'animate-spin' : ''}`} />
              Reset to Default
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={isSaving || isResetting}
              className="bg-[#087F75] hover:bg-[#06685F] text-white text-xs font-semibold shadow-sm"
            >
              {isSaving ? (
                <div className="flex items-center gap-1.5">
                  <div className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  <span>Saving...</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5" />
                  <span>Save Branding</span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {feedback && (
          <div
            className={`p-3 rounded-lg text-xs flex items-center space-x-2 border ${
              feedback.type === 'success'
                ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                : 'bg-rose-50 text-rose-800 border-rose-200'
            }`}
          >
            {feedback.type === 'success' ? (
              <Check className="h-4 w-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 text-rose-600 shrink-0" />
            )}
            <span className="font-medium">{feedback.message}</span>
          </div>
        )}

        {/* Agency / Workspace Name Input */}
        <div className="max-w-md space-y-1.5">
          <label className="text-xs font-semibold text-brand-ink flex items-center justify-between">
            <span>Workspace Display Name</span>
            <span className="text-[10px] text-brand-muted">Used in titles &amp; sidebar</span>
          </label>
          <Input
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            placeholder="e.g. Techosolution Agency"
            className="h-9 text-xs"
          />
        </div>

        {/* Three Upload Zones Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Zone 1: Main Workspace Logo */}
          <UploadZone
            title="Main Workspace Logo"
            subtitle="Displayed on light backgrounds, login screen, and sidebar expanded."
            acceptedFormats=".png,.svg,.jpg,.jpeg,.webp"
            maxSizeMb={5}
            currentValue={logoUrl}
            onSelect={setLogoUrl}
            backgroundType="light"
            workspaceName={workspaceName}
          />

          {/* Zone 2: Dark Mode / Compact Logo */}
          <UploadZone
            title="Dark Mode & Compact Icon"
            subtitle="Displayed in dark executive theme, collapsed sidebar, and mobile drawer."
            acceptedFormats=".png,.svg,.jpg,.jpeg,.webp"
            maxSizeMb={5}
            currentValue={darkLogoUrl}
            onSelect={setDarkLogoUrl}
            backgroundType="dark"
            workspaceName={workspaceName}
          />

          {/* Zone 3: Browser Favicon */}
          <UploadZone
            title="Browser Favicon"
            subtitle="Displayed in browser tabs, bookmarks, and mobile PWA shortcuts."
            acceptedFormats=".ico,.png,.svg"
            maxSizeMb={2}
            currentValue={faviconUrl}
            onSelect={setFaviconUrl}
            backgroundType="browser"
            workspaceName={workspaceName}
          />
        </div>

        {hasUnsavedChanges && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between text-xs text-amber-800">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-600 shrink-0" />
              <span>You have unsaved branding updates. Click "Save Branding" to apply changes live.</span>
            </div>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              className="bg-[#087F75] hover:bg-[#06685F] text-white text-xs font-semibold h-7 px-3 shrink-0"
            >
              Save Now
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
